import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, Truck } from 'lucide-react';
import { api } from '../lib/api';
import { SEO } from '../components/SEO';
import { VehicleCard } from '../components/VehicleCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../components/ui/breadcrumb';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'price_asc', label: 'Lowest Price' },
  { value: 'price_desc', label: 'Highest Price' },
];

const EMPTY_FILTERS = {
  search: '',
  vehicle_type: 'all',
  make: 'all',
  year_min: 'any',
  year_max: 'any',
  price_min: '',
  price_max: '',
};

const FilterPanel = ({ draft, setDraft, meta, onApply, onReset }) => (
  <div className="space-y-5">
    <div>
      <h3 className="font-heading text-lg tracking-wider text-[#24135F] mb-2">SEARCH</h3>
      <div className="relative">
        <Input
          data-testid="truck-filters-search-input"
          value={draft.search}
          onChange={(e) => setDraft((d) => ({ ...d, search: e.target.value }))}
          onKeyDown={(e) => e.key === 'Enter' && onApply()}
          placeholder="Search vehicles..."
          className="h-11 rounded-lg pr-10 focus-visible:ring-2 focus-visible:ring-[#E4002B]"
        />
        <Search className="absolute right-3 top-3 h-5 w-5 text-[#53565A]/70" />
      </div>
    </div>

    <div>
      <h3 className="font-heading text-lg tracking-wider text-[#24135F] mb-3">FILTERS</h3>
      <div className="space-y-4">
        <div>
          <label className="text-xs font-semibold text-[#53565A] block mb-1.5">Vehicle Type</label>
          <Select value={draft.vehicle_type} onValueChange={(v) => setDraft((d) => ({ ...d, vehicle_type: v }))}>
            <SelectTrigger data-testid="truck-filters-vehicle-type-select" className="h-11 rounded-lg">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {meta.vehicle_types.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold text-[#53565A] block mb-1.5">Make</label>
          <Select value={draft.make} onValueChange={(v) => setDraft((d) => ({ ...d, make: v }))}>
            <SelectTrigger data-testid="truck-filters-make-select" className="h-11 rounded-lg">
              <SelectValue placeholder="All Makes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Makes</SelectItem>
              {meta.makes.map((m) => (
                <SelectItem key={m} value={m}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold text-[#53565A] block mb-1.5">Year</label>
          <div className="grid grid-cols-2 gap-2">
            <Select value={draft.year_min} onValueChange={(v) => setDraft((d) => ({ ...d, year_min: v }))}>
              <SelectTrigger data-testid="truck-filters-year-from-select" className="h-11 rounded-lg">
                <SelectValue placeholder="From" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">From</SelectItem>
                {meta.years.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={draft.year_max} onValueChange={(v) => setDraft((d) => ({ ...d, year_max: v }))}>
              <SelectTrigger data-testid="truck-filters-year-to-select" className="h-11 rounded-lg">
                <SelectValue placeholder="To" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">To</SelectItem>
                {meta.years.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-[#53565A] block mb-1.5">Price</label>
          <div className="grid grid-cols-2 gap-2">
            <Input
              data-testid="truck-filters-price-min-select"
              type="number"
              min="0"
              placeholder="Min"
              value={draft.price_min}
              onChange={(e) => setDraft((d) => ({ ...d, price_min: e.target.value }))}
              className="h-11 rounded-lg focus-visible:ring-2 focus-visible:ring-[#E4002B]"
            />
            <Input
              data-testid="truck-filters-price-max-select"
              type="number"
              min="0"
              placeholder="Max"
              value={draft.price_max}
              onChange={(e) => setDraft((d) => ({ ...d, price_max: e.target.value }))}
              className="h-11 rounded-lg focus-visible:ring-2 focus-visible:ring-[#E4002B]"
            />
          </div>
        </div>
      </div>
    </div>

    <div className="flex flex-col gap-2 pt-1">
      <Button
        data-testid="truck-filters-apply-button"
        onClick={onApply}
        className="rounded-full bg-[#E4002B] text-white hover:bg-[#E4002B]/90 h-11 font-bold tracking-wider transition-colors duration-150"
      >
        APPLY FILTERS
      </Button>
      <Button
        data-testid="truck-filters-reset-button"
        variant="outline"
        onClick={onReset}
        className="rounded-full border border-[#D9D9D6] bg-white text-[#24135F] hover:bg-[#D9D9D6]/15 h-11 font-bold tracking-wider transition-colors duration-150"
      >
        RESET FILTERS
      </Button>
    </div>
  </div>
);

export default function Trucks() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ makes: [], vehicle_types: [], years: [] });
  const [sort, setSort] = useState(searchParams.get('sort') || 'newest');
  const [mobileOpen, setMobileOpen] = useState(false);

  const [applied, setApplied] = useState({
    ...EMPTY_FILTERS,
    search: searchParams.get('search') || '',
    vehicle_type: searchParams.get('type') || 'all',
    make: searchParams.get('make') || 'all',
  });
  const [draft, setDraft] = useState(applied);

  useEffect(() => {
    api.get('/meta/filters').then((res) => setMeta(res.data)).catch(() => {});
  }, []);

  const fetchVehicles = useCallback((filters, sortVal) => {
    setLoading(true);
    const params = { sort: sortVal, limit: 48 };
    if (filters.search) params.search = filters.search;
    if (filters.vehicle_type !== 'all') params.vehicle_type = filters.vehicle_type;
    if (filters.make !== 'all') params.make = filters.make;
    if (filters.year_min !== 'any') params.year_min = filters.year_min;
    if (filters.year_max !== 'any') params.year_max = filters.year_max;
    if (filters.price_min) params.price_min = filters.price_min;
    if (filters.price_max) params.price_max = filters.price_max;
    api
      .get('/vehicles', { params })
      .then((res) => {
        setVehicles(res.data.vehicles);
        setTotal(res.data.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchVehicles(applied, sort);
  }, [applied, sort, fetchVehicles]);

  const handleApply = () => {
    setApplied(draft);
    setMobileOpen(false);
    const sp = {};
    if (draft.search) sp.search = draft.search;
    if (draft.vehicle_type !== 'all') sp.type = draft.vehicle_type;
    if (draft.make !== 'all') sp.make = draft.make;
    if (sort !== 'newest') sp.sort = sort;
    setSearchParams(sp, { replace: true });
  };

  const handleReset = () => {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setSearchParams({}, { replace: true });
    setMobileOpen(false);
  };

  return (
    <>
      <SEO
        title="Trucks for Sale"
        description="Browse quality used trucks and trailers for sale at Higgins Truck Sales. Prime movers, rigids, curtainsiders and more - available across Australia."
        path="/trucks"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <Breadcrumb data-testid="page-breadcrumb">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Trucks for Sale</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex flex-wrap items-center justify-between gap-4 mt-4 mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-wider text-[#24135F]">TRUCKS FOR SALE</h1>
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="truck-filters-open-drawer-button"
                  className="lg:hidden rounded-full border-[#D9D9D6] h-10 px-4 text-xs font-bold tracking-wider"
                >
                  <SlidersHorizontal className="h-4 w-4 mr-2" /> FILTERS
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[320px] overflow-y-auto">
                <SheetTitle className="font-heading text-2xl tracking-wider mb-4">FILTER VEHICLES</SheetTitle>
                <FilterPanel draft={draft} setDraft={setDraft} meta={meta} onApply={handleApply} onReset={handleReset} />
              </SheetContent>
            </Sheet>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#53565A] font-medium hidden sm:inline">Sort by:</span>
              <Select
                value={sort}
                onValueChange={(v) => {
                  setSort(v);
                }}
              >
                <SelectTrigger data-testid="truck-sort-select" className="h-10 rounded-lg w-[150px] text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="lg:grid lg:grid-cols-[300px_1fr] lg:gap-8">
          <aside className="hidden lg:block">
            <div className="sticky top-24 bg-white border border-[#D9D9D6] rounded-xl p-5">
              <FilterPanel draft={draft} setDraft={setDraft} meta={meta} onApply={handleApply} onReset={handleReset} />
            </div>
          </aside>

          <div>
            {!loading && (
              <p className="text-sm text-[#53565A] mb-4" data-testid="truck-results-count">
                {total} vehicle{total !== 1 ? 's' : ''} found
              </p>
            )}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="space-y-3">
                    <Skeleton className="aspect-[4/3] rounded-xl" />
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-6 w-1/2" />
                  </div>
                ))}
              </div>
            ) : vehicles.length === 0 ? (
              <div className="text-center py-20 bg-[#D9D9D6]/15 rounded-xl border border-[#D9D9D6]" data-testid="truck-empty-state">
                <Truck className="h-12 w-12 text-[#D9D9D6] mx-auto mb-4" />
                <h3 className="font-heading text-2xl tracking-wider text-[#24135F]">NO VEHICLES FOUND</h3>
                <p className="text-sm text-[#53565A] mt-2 mb-6">Try adjusting your search or filters.</p>
                <Button
                  onClick={handleReset}
                  variant="outline"
                  className="rounded-full border-[#D9D9D6] h-10 px-6 text-xs font-bold tracking-wider"
                >
                  RESET FILTERS
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="truck-results-grid">
                {vehicles.map((v) => (
                  <VehicleCard key={v.id} vehicle={v} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
